import { axios } from '@/api/axios.ts';
import { createQueryKeys } from '@lukemorales/query-key-factory';
import type {
  TClientRoomAttributesPayload,
  TRoom,
  TRoomAttribute,
  TRoomMaintenanceTask,
  TRoomMaintenanceTaskPayload,
  TClientTree,
  TRoomSortOption,
  TAttributeSortOption,
} from '@/services/client-room-management/client-room-management.types.ts';

export const clientRoomManagementApi = {
  //Client Rooms
  getClientRooms: async (payload: { id: number; sort?: TRoomSortOption }) => {
    const { id, sort } = payload;
    const response = await axios.get<TRoom[]>(`clients/${id}/rooms`, {
      params: { sort: sort || 'CREATED_AT|DESC' },
    });

    return response.data;
  },
  loadDefaultTemplate: async (payload: { id: number }) => {
    const response = await axios.post<{ queued: boolean }>(
      `clients/${payload.id}/rooms/load-defaults`,
    );

    return response.data;
  },
  reorderClientRooms: async (payload: { id: number; userRoomIds: number[] }) => {
    const { id, userRoomIds } = payload;
    const response = await axios.patch<{ success: boolean; updated: number }>(
      `clients/${id}/rooms/reorder`,
      { user_room_ids: userRoomIds },
    );

    return response.data;
  },
  getClientRoomById: async (payload: { id: number }) => {
    const { id } = payload;
    const response = await axios.get<TRoom>(`clients/rooms/${id}`);

    return response.data;
  },
  assignClientRoom: async (payload: { id: number; roomId: number; custom_name: string }) => {
    const { id, roomId, custom_name } = payload;
    const response = await axios.post<TRoom>(`clients/${id}/rooms`, {
      custom_name,
      room_id: roomId,
    });

    return response.data;
  },
  updateAssignClientRoomById: async (payload: {
    id: number;
    roomId: number;
    userRoomId: number;
    custom_name: string;
    newRoomId: number;
  }) => {
    const { id, roomId, userRoomId, custom_name, newRoomId } = payload;
    const response = await axios.patch<TRoom>(`clients/${id}/rooms/${roomId}/${userRoomId}`, {
      custom_name,
      room_id: newRoomId,
    });

    return response.data;
  },
  unassignClientRoomById: async (payload: { id: number; roomId: number; userRoomId: number }) => {
    const { id, roomId, userRoomId } = payload;
    const response = await axios.delete<TRoom>(`clients/${id}/rooms/${roomId}/${userRoomId}`);

    return response.data;
  },
  //Client Room Attributes
  getClientRoomAttributes: async (payload: {
    id: number;
    roomId: number;
    userRoomId: number;
    search?: string;
    sort?: TAttributeSortOption;
  }) => {
    const { id, roomId, userRoomId, search, sort } = payload;
    const params: Record<string, string> = {};
    if (search) params.search = search;
    if (sort) params.sort = sort;

    const response = await axios.get<TRoomAttribute[]>(
      `clients/${id}/rooms/${roomId}/${userRoomId}/attributes`,
      { params },
    );

    return response.data;
  },
  getClientRoomAttributeById: async (payload: { id: number; attributeId: number }) => {
    const { id, attributeId } = payload;
    const response = await axios.get<TRoomAttribute>(
      `clients/rooms/${id}/attributes/${attributeId}`,
    );

    return response.data;
  },
  createClientRoomAttribute: async (payload: {
    id: number;
    roomId: number;
    userRoomId: number;
    data: TClientRoomAttributesPayload;
  }) => {
    const { id, roomId, userRoomId, data } = payload;
    const response = await axios.post<TRoomAttribute>(
      `clients/${id}/rooms/${roomId}/${userRoomId}/attributes`,
      data,
    );

    return response.data;
  },
  updateClientRoomAttribute: async (payload: {
    id: number;
    roomId: number;
    userRoomId: number;
    attributeId: number;
    data: Partial<TClientRoomAttributesPayload>;
  }) => {
    const { id, roomId, userRoomId, attributeId, data } = payload;
    const response = await axios.patch<TRoomAttribute>(
      `clients/${id}/rooms/${roomId}/${userRoomId}/attributes/${attributeId}`,
      data,
    );

    return response.data;
  },
  deleteClientRoomAttributeById: async (payload: {
    id: number;
    roomId: number;
    userRoomId: number;
    attributeId: number;
  }) => {
    const { id, roomId, userRoomId, attributeId } = payload;
    const response = await axios.delete<TRoomAttribute>(
      `clients/${id}/rooms/${roomId}/${userRoomId}/attributes/${attributeId}`,
    );

    return response.data;
  },
  enableClientRoomAttribute: async (payload: {
    id: number;
    roomId: number;
    userRoomId: number;
    attributeId: number;
  }) => {
    const { id, roomId, userRoomId, attributeId } = payload;
    const response = await axios.patch<TRoomAttribute>(
      `clients/${id}/rooms/${roomId}/${userRoomId}/attributes/${attributeId}/enable`,
    );

    return response.data;
  },
  disableClientRoomAttribute: async (payload: {
    id: number;
    roomId: number;
    userRoomId: number;
    attributeId: number;
  }) => {
    const { id, roomId, userRoomId, attributeId } = payload;
    const response = await axios.patch<TRoomAttribute>(
      `clients/${id}/rooms/${roomId}/${userRoomId}/attributes/${attributeId}/disable`,
    );

    return response.data;
  },
  //Client Room Maintenance Tasks
  getClientRoomMaintenanceTasks: async (payload: {
    id: number;
    roomId: number;
    userRoomId: number;
  }) => {
    const { id, roomId, userRoomId } = payload;
    // Current and next year; the range used to be hardcoded to 2025-2026,
    // which kept showing last year's dates and would go blank in 2027.
    const currentYear = new Date().getFullYear();
    const response = await axios.get<TRoomMaintenanceTask[]>(
      `clients/${id}/rooms/${roomId}/${userRoomId}/maintenance-tasks?year=${currentYear}-${
        currentYear + 1
      }`,
    );

    return response.data;
  },
  getClientRoomMaintenanceTaskById: async (payload: { id: number; taskId: number }) => {
    const { id, taskId } = payload;
    const response = await axios.get<TRoomMaintenanceTask>(
      `clients/rooms/${id}/maintenance-tasks/${taskId}`,
    );

    return response.data;
  },
  createClientRoomMaintenanceTask: async (payload: {
    id: number;
    roomId: number;
    userRoomId: number;
    data: TRoomMaintenanceTaskPayload;
  }) => {
    const { id, roomId, userRoomId, data } = payload;
    const response = await axios.post<TRoomMaintenanceTask>(
      `clients/${id}/rooms/${roomId}/${userRoomId}/maintenance-tasks`,
      data,
    );

    return response.data;
  },
  updateClientRoomMaintenanceTaskById: async (payload: {
    id: number;
    roomId: number;
    userRoomId: number;
    taskId: number;
    data: Partial<TRoomMaintenanceTaskPayload>;
  }) => {
    const { id, roomId, userRoomId, taskId, data } = payload;
    const response = await axios.patch<TRoomMaintenanceTask>(
      `clients/${id}/rooms/${roomId}/${userRoomId}/maintenance-tasks/${taskId}`,
      data,
    );

    return response.data;
  },
  deleteClientRoomMaintenanceTaskById: async (payload: {
    id: number;
    roomId: number;
    userRoomId: number;
    taskId: number;
  }) => {
    const { id, roomId, userRoomId, taskId } = payload;
    const response = await axios.delete<TRoomMaintenanceTask>(
      `clients/${id}/rooms/${roomId}/${userRoomId}/maintenance-tasks/${taskId}`,
    );

    return response.data;
  },
  //Client Room Tree
  getTreeForUser: async (payload: { id: number }) => {
    const { id } = payload;
    const response = await axios.get<TClientTree>(`clients/${id}/rooms/tree`);

    return response.data;
  },
};

export const clientRoomManagementQueries = createQueryKeys('clientRoomManagement', {
  getClientRooms: (id: number, sort?: TRoomSortOption) => ({
    queryKey: ['client', id, 'rooms', { sort }],
    queryFn: () => clientRoomManagementApi.getClientRooms({ id, sort }),
  }),
  getClientRoomById: (id: number) => ({
    queryKey: ['client', 'rooms', id],
    queryFn: () => clientRoomManagementApi.getClientRoomById({ id }),
  }),
  getClientRoomAttributes: (
    id: number,
    roomId: number,
    userRoomId: number,
    search?: string,
    sort?: TAttributeSortOption,
  ) => ({
    queryKey: ['client', id, 'rooms', roomId, userRoomId, 'attributes', { search, sort }],
    queryFn: () =>
      clientRoomManagementApi.getClientRoomAttributes({ id, roomId, userRoomId, search, sort }),
  }),
  getClientRoomAttributeById: (id: number, attributeId: number) => ({
    queryKey: ['clients', 'rooms', id, 'attributes', attributeId],
    queryFn: () => clientRoomManagementApi.getClientRoomAttributeById({ id, attributeId }),
  }),
  getClientRoomMaintenanceTasks: (id: number, roomId: number, userRoomId: number) => ({
    queryKey: ['client', id, 'rooms', roomId, userRoomId, 'maintenance-tasks'],
    queryFn: () =>
      clientRoomManagementApi.getClientRoomMaintenanceTasks({ id, roomId, userRoomId }),
  }),
  getClientRoomMaintenanceTaskById: (id: number, taskId: number) => ({
    queryKey: ['clients', 'rooms', id, 'maintenance-tasks', taskId],
    queryFn: () => clientRoomManagementApi.getClientRoomMaintenanceTaskById({ id, taskId }),
  }),
  getTreeForUser: (id: number) => ({
    queryKey: ['clients', id, 'rooms', 'tree'],
    queryFn: () => clientRoomManagementApi.getTreeForUser({ id }),
  }),
});
